import { CsvComponent } from './csv/csv.component';
import { isEqual, toLower, values } from 'lodash';
import { FormControlValueChangedEvent } from './models/form-value-changed-event';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { DropdownControlComponent } from './dropdown-control/dropdown-control.component';
import { ChipControlComponent } from './chip-control/chip-control.component';
import { DynamicDictComponent } from './dynamic-dict/dynamic-dict.component';
import { CsvUtilsComponent } from './csv-utils/csv-utils.component';

@Injectable({
  providedIn: 'root',
})
export class JsonDynamicComponentService {
  externalParams: any;
  mainFormGroup: FormGroup;
  csvControls: { [id: string]: CsvComponent } = {};
  dropdownControls: { [id: string]: DropdownControlComponent } = {};
  chipControls: { [id: string]: ChipControlComponent } = {};
  dynamicDictControls: { [id: string]: DynamicDictComponent } = {};
  dropdownControlsInitialValues: { [name: string]: any } = {};
  chipControlsInitialValues: { [name: string]: any } = {};
  csvUtilsControls: { [id: string]: CsvUtilsComponent } = {};

  private formValueChanged = new Subject<FormControlValueChangedEvent>();
  formValueChanged$ = this.formValueChanged.asObservable();

  emitFormValueChanged(event: FormControlValueChangedEvent) {
    this.formValueChanged.next(event);
  }

  addCsvControl(id: string, control: CsvComponent) {
    this.csvControls[id] = control;
  }

  addDropdownControl(id: string, control: DropdownControlComponent) {
    this.dropdownControls[id] = control;
  }

  refreshDropdownValues(property: string) {
    values(this.dropdownControls)?.forEach((control) => {
      if (control?.currentConfig.property === property) {
        control?.updateAndSelectAll(control.currentConfig);
      }
    });
    values(this.chipControls)?.forEach((control) => {
      if (control?.currentConfig.property === property) {
        control?.updateAndSelectAll(control.currentConfig);
      }
    });
  }

  async updateAllowedColumnsForAllCsvs(): Promise<boolean> {
    const results = await Promise.all(
      values(this.csvControls)?.map((control) => control?.setAllowedColumns()),
    );
    return results.every((result) => result);
  }

  async validateSelectedValuesForAllDropdowns(
    configFilter: Function,
    hasModelChanged: boolean = false,
    formValueWithDefaults?: any,
  ): Promise<boolean> {
    const results = await Promise.all([
      this.validateForDropdowns(configFilter, hasModelChanged),
      this.validateForDynamicDicts(
        configFilter,
        hasModelChanged,
        formValueWithDefaults,
      ),
    ]);
    return results.every((result) => result);
  }

  private async validateForDropdowns(
    configFilter: Function,
    hasModelChanged: boolean,
  ): Promise<boolean> {
    const dropdownPromises = values(this.dropdownControls)?.map((control) => {
      return control?.validateSelectedValues(
        control.currentConfig,
        configFilter,
        hasModelChanged,
      );
    });
    const chipPromises = values(this.chipControls)?.map((control) => {
      return control?.validateSelectedValues(
        control.currentConfig,
        configFilter,
        hasModelChanged,
      );
    });
    const results = await Promise.all([
      ...(dropdownPromises || []),
      ...(chipPromises || []),
    ]);
    return results.every((result) => result);
  }

  private async validateForDynamicDicts(
    configFilter: Function,
    hasModelChanged: boolean,
    formValueWithDefaults?: any,
  ): Promise<boolean> {
    const validationPromises = values(this.dynamicDictControls)?.map(
      (control) => {
        return control?.validateDropdownSelectedValues(
          configFilter,
          hasModelChanged,
          formValueWithDefaults,
        );
      },
    );

    const results = await Promise.all(validationPromises);
    return results.every((result) => result);
  }

  addDynamicDictControl(id: string, control: DynamicDictComponent) {
    this.dynamicDictControls[id] = control;
  }

  removeCsvControl(id: string) {
    delete this.csvControls[id];
  }

  removeDynamicDictControl(id: string) {
    delete this.dynamicDictControls[id];
  }

  removeDropdownControl(id: string) {
    delete this.dropdownControls[id];
  }

  addChipControl(id: string, control: ChipControlComponent) {
    this.chipControls[id] = control;
  }

  removeChipControl(id: string) {
    delete this.chipControls[id];
  }

  saveAndValidateAllCsvs(): Promise<boolean> {
    const savePromises = values(this.csvControls)?.map(async (control) => {
      return control?.updateCSV().finally(() => {
        control?.validateSave();
        control?.validateCSVSelection();
      });
    });

    return Promise.all(savePromises).then((results) => {
      return results.every((result) => result);
    });
  }

  anyCsvStillLoading(): boolean {
    return values(this.csvControls)?.some((control) => control?.loading);
  }

  anyDropdownStillLoading(): boolean {
    return (
      values(this.dropdownControls)?.some(
        (control) => control?.currentConfig.loading,
      ) ||
      values(this.chipControls)?.some(
        (control) => control?.currentConfig.loading,
      )
    );
  }

  isAnyCsvNotSaved(): { isAnyCsvNotSaved: boolean; csvId: string } {
    const controls = values(this.csvControls);
    const control = controls.find(
      (control) => !control?.isSaveButtonDisabled(),
    );
    return control
      ? { isAnyCsvNotSaved: true, csvId: control.id }
      : { isAnyCsvNotSaved: false, csvId: '' };
  }

  validateAllCsvsForSave() {
    values(this.csvControls)?.forEach((control) => {
      control?.validateSave();
    });
  }

  validateAllDynamicDictControls() {
    values(this.dynamicDictControls)?.forEach((control) => {
      control?.validate();
    });
  }

  deleteDependentKey(propertyName, controlName) {
    values(this.dynamicDictControls)?.forEach((control) => {
      if (control.currentConfig.property === propertyName) {
        control?.deleteControl(controlName);
      }
    });
  }

  isAnyControlWithNoCSVSelection(): {
    isAnyCsvNotSelected: boolean;
    csvId: string;
  } {
    const controls = values(this.csvControls);
    const control = controls.find((control) => {
      return control?.formGroup.value.data.length === 0;
    });
    return control
      ? { isAnyCsvNotSelected: true, csvId: control.id }
      : { isAnyCsvNotSelected: false, csvId: '' };
  }

  validateAllCsvsForNoSelection() {
    values(this.csvControls)?.forEach((control) => {
      control?.validateCSVSelection();
    });
  }

  addDropdownControlInitialValues(control: DropdownControlComponent) {
    this.dropdownControlsInitialValues[control?.id] =
      control?.parentFormGroup?.get(control?.currentConfig?.property)?.value ||
      null;
  }

  removeDropdownControlInitialValues(control: DropdownControlComponent) {
    delete this.dropdownControlsInitialValues[control?.id];
  }

  addChipControlInitialValues(control: ChipControlComponent) {
    this.chipControlsInitialValues[control?.id] =
      control?.parentFormGroup?.get(control?.currentConfig?.property)?.value ??
      null;
  }

  removeChipControlInitialValues(control: ChipControlComponent) {
    delete this.chipControlsInitialValues[control?.id];
  }

  updateCSV(controlName: string, path: string, data: any) {
    values(this.csvControls)?.forEach((control: CsvComponent) => {
      if (
        isEqual(control?.config?.property, controlName) &&
        isEqual(control?.config?.path, path)
      ) {
        control?.createCSVFormGroup(data, () => {});
        control?.updateCSV();
      }
    });
  }

  async waitForAllCSVToLoad() {
    const loadedPromises = values(this.csvControls).map((control) => {
      return new Promise<void>((resolve) => {
        const isLoaded = () => {
          if (!control.loading) {
            resolve();
          } else {
            setTimeout(isLoaded, 100);
          }
        };
        isLoaded();
      });
    });
    (await Promise.all(loadedPromises)).every((result) => result);
  }

  validateSelectedCSVAllColumns(fileName: string) {
    values(this.csvControls)?.forEach((control: CsvComponent) => {
      if (
        isEqual(toLower(control.commonUtils.dynamicDictService.key), fileName)
      ) {
        control?.validateAllColumns(control.displayedColumns);
      }
    });
  }

  isAnyCSVControlWithInvalidColumns(): {
    isAnyCSVControlColumnsInvalid: boolean;
    csvId: string;
  } {
    const control = values(this.csvControls).find(
      (control) => !control?.validateAllColumns(control.displayedColumns),
    );
    return control
      ? { isAnyCSVControlColumnsInvalid: true, csvId: control.id }
      : { isAnyCSVControlColumnsInvalid: false, csvId: '' };
  }

  addCsvUtilsControl(id: string, control: CsvUtilsComponent) {
    this.csvUtilsControls[id] = control;
  }

  updateCsvFileStatsMsg(controlName: string) {
    const control = values(this.csvUtilsControls).find(
      (control) => control?.fileName === controlName,
    );
    control?.updateCsvFileStatsMsg();
  }
}
